import React from 'react';
import { PaperclipIcon, Reply } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ChatFile } from '@/types/chat';

interface ChatBubbleProps {
  messageId: string;
  align: 'start' | 'end';
  bubbleClassName: string;
  actionsClassName?: string;
  header: React.ReactNode;
  replySnippet?: React.ReactNode;
  body: React.ReactNode;
  attachments?: React.ReactNode;
  actions?: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  messageId,
  align,
  bubbleClassName,
  actionsClassName,
  header,
  replySnippet,
  body,
  attachments,
  actions,
  onMouseEnter,
  onMouseLeave,
}) => (
  <div
    id={`message-${messageId}`}
    className={cn('flex mb-4 w-full', align === 'start' ? 'justify-start' : 'justify-end')}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <div className="relative w-full max-w-[80%] min-w-0">
      <div
        className={cn(
          'relative w-full min-w-0 rounded-lg shadow-sm break-words hyphens-auto [overflow-wrap:anywhere]',
          bubbleClassName
        )}
      >
        {header}
        {replySnippet}
        {body}
        {attachments}
      </div>

      {actions && (
        <div className={actionsClassName}>
          {actions}
        </div>
      )}
    </div>
  </div>
);

interface ChatBubbleHeaderProps {
  label: React.ReactNode;
  timestamp: React.ReactNode;
  icon?: React.ReactNode;
}

export const ChatBubbleHeader: React.FC<ChatBubbleHeaderProps> = ({
  label,
  timestamp,
  icon,
}) => (
  <div className="flex items-center mb-1 text-xs">
    {icon}
    <span className="font-medium mr-2">{label}</span>
    <span className="text-neutral-500 dark:text-neutral-400 mr-2">
      {timestamp}
    </span>
  </div>
);

interface ChatReplySnippetProps {
  authorLabel: React.ReactNode;
  content: string;
  title: string;
  onClick: () => void;
}

export const ChatReplySnippet: React.FC<ChatReplySnippetProps> = ({
  authorLabel,
  content,
  title,
  onClick,
}) => (
  <div
    className="mb-2 p-2 rounded-md border border-neutral-300 dark:border-neutral-600 bg-neutral-200 dark:bg-neutral-700 text-xs text-neutral-700 dark:text-neutral-300 cursor-pointer hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
    onClick={onClick}
    title={title}
  >
    <div className="flex items-center mb-1 font-medium text-neutral-600 dark:text-neutral-400">
      <Reply className="h-3 w-3 mr-1.5 flex-shrink-0" />
      {authorLabel}
    </div>
    <p className="line-clamp-3 break-words">{content}</p>
  </div>
);

interface ChatAttachmentListProps {
  files: ChatFile[];
  itemClassName: string;
  iconClassName: string;
  textClassName: string;
}

export const ChatAttachmentList: React.FC<ChatAttachmentListProps> = ({
  files,
  itemClassName,
  iconClassName,
  textClassName,
}) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1">
      {files.map((file) => (
        <div
          key={file.id}
          className={cn('flex items-center p-1.5 rounded-md text-xs', itemClassName)}
        >
          <PaperclipIcon className={cn('h-3.5 w-3.5 mr-1.5 flex-shrink-0', iconClassName)} />
          <span className={cn('truncate flex-1', textClassName)} title={file.name}>
            {file.name}
          </span>
        </div>
      ))}
    </div>
  );
};
